import { PrinterType } from '@core/services/system/receipt-printers/enum/printer-type.enum';

/**
 * Raised by a printer service when the underlying plugin reports an error
 * after the print payload has already been written to the device, so the
 * physical print may have completed successfully.
 *
 * Typical sources on Wi-Fi printers (e.g. Star TSP143IIIW): the printer
 * accepts the data and prints it, then drops the TCP connection before
 * the client can read the final status. The plugin surfaces this as a
 * generic disconnect or port-open failure, even though the print did
 * happen.
 *
 * Retrying these errors is unsafe — the next attempt would print a
 * duplicate. The print queue must therefore treat them as terminal and
 * surface the uncertainty to the operator instead of silently retrying.
 *
 * With the Star plugin >= 7.3.2 the error also carries the physical reason
 * reported after the write (e.g. the paper ran out mid-receipt).
 */
export class PrinterUncertainError extends Error {
  constructor(
    public readonly printerType: PrinterType,
    public readonly originalMessage: string,
    public readonly originalError?: unknown,
    public readonly reason?: string,
    /** True when the Star plugin before 7.3.2 reported this failure as a successful print (iOS). */
    public readonly legacyIosSuccess: boolean = false
  ) {
    super(`[${printerType}] Printer state uncertain after write: ${originalMessage}`);
    this.name = 'PrinterUncertainError';

    // Preserve prototype chain across the TS down-level emit so that
    // `error instanceof PrinterUncertainError` works reliably at consumer sites.
    Object.setPrototypeOf(this, PrinterUncertainError.prototype);
  }
}
